// Tab-scoped recovery contains responses only, never invitation credentials or question content.
const PREFIX = 'assessment-draft-v1:'
const TTL = 24 * 60 * 60 * 1000
const MAX_BYTES = 128 * 1024
const MAX_DRAFTS = 10

function storage() { try { return window.sessionStorage } catch (_) { return null } }
export function clearDraft(id) { try { storage().removeItem(PREFIX + id) } catch (_) {} }
export function readDraft(id) {
  try {
    const raw = storage().getItem(PREFIX + id)
    if (!raw || raw.length > MAX_BYTES) { clearDraft(id); return null }
    const draft = JSON.parse(raw)
    if (!Number.isInteger(draft.revision) || !Number.isFinite(draft.savedAt) || Date.now() - draft.savedAt > TTL || draft.savedAt > Date.now() || !draft.responses || typeof draft.responses !== 'object') {
      clearDraft(id); return null
    }
    return draft
  } catch (_) { clearDraft(id); return null }
}
export function writeDraft(data, responses) {
  try {
    const target = storage()
    if (!target) { return false }
    const keys = Object.keys(target).filter(key => key.startsWith(PREFIX))
    keys.forEach(key => { readDraft(key.slice(PREFIX.length)) })
    const remaining = Object.keys(target).filter(key => key.startsWith(PREFIX)).sort((a, b) => JSON.parse(target.getItem(a)).savedAt - JSON.parse(target.getItem(b)).savedAt)
    while (remaining.length >= MAX_DRAFTS) { target.removeItem(remaining.shift()) }
    const value = JSON.stringify({ revision: data.revision, deadlineAt: data.deadlineAt, responses, savedAt: Date.now() })
    if (value.length > MAX_BYTES) { return false }
    target.setItem(PREFIX + data.sessionId, value)
    return true
  } catch (_) { return false }
}

// Object-key ordering is irrelevant for questionnaire responses; answer list order is stable.
export function sameResponses(a, b) {
  const normalise = value => Array.isArray(value) ? value.map(normalise) : value && typeof value === 'object'
    ? Object.keys(value).sort().reduce((result, key) => ({ ...result, [key]: normalise(value[key]) }), {}) : value
  return JSON.stringify(normalise(a)) === JSON.stringify(normalise(b))
}

// Treat browser storage as untrusted. A corrupt draft must not prevent loading the server copy.
export function validDraftResponses(session, responses) {
  if (!responses || typeof responses !== 'object') { return false }
  if (session.format === 'choice' || session.format === 'likely') {
    if (!Array.isArray(responses) || responses.length > session.questions.length) { return false }
    const seen = new Set()
    return responses.every(response => {
      if (!response || !response.answer || typeof response.answer !== 'object' || seen.has(response.questionUuid)) { return false }
      seen.add(response.questionUuid)
      const question = session.questions.find(q => q.uuid === response.questionUuid)
      if (!question) { return false }
      const ids = question.answers.map(answer => answer.uuid)
      if (session.format === 'choice') { return ids.includes(response.answer.uuid) }
      const selection = response.answer.likely
      return selection && [selection.most, selection.least].every(id => id === null || ids.includes(id)) &&
        (selection.most === null || selection.least === null || selection.most !== selection.least)
    })
  }
  if (Array.isArray(responses)) { return responses.length === 0 }
  const drives = session.format === 'drives'
  if (!drives && session.format !== 'personality') { return false }
  const keys = drives ? ['learning', 'team', 'money', 'innovation', 'rewards', 'purpose', 'recognition', 'responsibility', 'security'] : session.statements.map(statement => statement.reference)
  return Object.entries(responses).every(([key, value]) => keys.includes(key) && Number.isInteger(value) && value >= (drives ? 0 : 1) && value <= 5) &&
    (!drives || Object.values(responses).reduce((sum, value) => sum + value, 0) <= 20)
}
